/**
 * All money crosses the wire as integer cents. These helpers are the only place
 * that converts, so a stray float can't creep into a total.
 */

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function formatCents(cents: number): string {
  return formatter.format(cents / 100);
}

/** Compact form for tight rows: $1,234 when whole, $12.34 when not. */
export function formatCentsShort(cents: number): string {
  return cents % 100 === 0
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(cents / 100)
    : formatCents(cents);
}

/**
 * Parses what a person actually types: "12", "12.5", "$1,234.56", " 40 ".
 * Returns null for anything unparseable so the caller can show a message
 * instead of silently recording a zero.
 */
export function parseAmountToCents(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (cleaned === '' || !/^-?\d*\.?\d*$/.test(cleaned) || cleaned === '.' || cleaned === '-') {
    return null;
  }

  const negative = cleaned.startsWith('-');
  const [whole = '0', fraction = ''] = cleaned.replace('-', '').split('.');

  // Round rather than truncate so "0.005" becomes a cent, not nothing.
  const padded = `${fraction}00`.slice(0, 3);
  const cents = Number(whole) * 100 + Math.round(Number(padded) / 10);

  if (!Number.isFinite(cents)) return null;
  return negative ? -cents : cents;
}

/** Value for an <input> being edited, without currency symbols. */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

export function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
